import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ForceGraph2D from 'react-force-graph-2d';
import { toast } from 'sonner';
import { GraphSearchBar } from './GraphSearchBar';
import { PathSearchPanel } from './PathSearchPanel';
import { GraphLegendPanel } from './GraphLegendPanel';
import { DocBadgePopover } from './DocBadgePopover';
import { GraphSidebar } from './GraphSidebar';
import { useResizablePanel } from './useResizablePanel';
import { useGraphStorageContext } from '../../contexts/GraphStorageContext';
import { seedGraph, expandNeighbors, findPaths } from '../../lib/graphApi';
import type { FindPathsResult, SinglePath } from '../../lib/graphApi';
import { searchEntities, type ExploreSearchResult } from '../../lib/exploreApi';
import { nodeColor } from '../../lib/entityColors';
import {
  getVirusTotalUrl,
  getNvdUrl,
  getMitreAttackUrl,
} from '../ioc/iocDetailHelpers';
import { relativeDate } from '../sidebar/FolderTree';
import {
  useForceGraph,
  NODE_COLORS,
  edgeColor,
  sourceEdgeColor,
  resolveKnowledgeSource,
  KNOWLEDGE_SOURCE_LABELS,
  type EdgeColorMode,
  type GraphNode,
  type GraphLink,
  type FGNode,
  type FGLink,
  type FGRef,
} from './useForceGraph';
import type { AccumulatedNode, AccumulatedGraph, AccumulatedEdge, SavedGraph } from '../../types';
import { classifyIOC, type IOCType } from '../../lib/iocClassifier';
import { serializeGraphToPrompt } from '../../lib/graphSerializer';

const ALL_TYPES = ['MalwareFamily', 'ThreatActor', 'CVE', 'IOC', 'AttackTechnique'];

// ── Expansion settings ──────────────────────────────────────────────────────

export interface ExpansionSettings {
  depth: 1 | 2;
  includeLabels: string[];
  excludeDocuments: boolean;
  maxNeighbors: number;
  dateFrom: string;
  dateTo: string;
}

const DEFAULT_EXPANSION_SETTINGS: ExpansionSettings = {
  depth: 1,
  includeLabels: [],
  excludeDocuments: true,
  maxNeighbors: 20,
  dateFrom: '',
  dateTo: '',
};

function loadExpansionSettings(): ExpansionSettings {
  try {
    const raw = localStorage.getItem('graph-expansion-settings');
    if (raw) return { ...DEFAULT_EXPANSION_SETTINGS, ...JSON.parse(raw) };
  } catch { /* ignore */ }
  return DEFAULT_EXPANSION_SETTINGS;
}

// ── Tooltip property renderers ──────────────────────────────────────────────

const PROP_LABELS: Record<string, string> = {
  ioc_type: 'IOC Type',
  infrastructure: 'Infrastructure',
  cvss_score: 'CVSS',
  cvss_severity: 'Severity',
  published_date: 'Published',
  in_cisa_kev: 'CISA KEV',
  cwe_ids: 'CWE',
  country: 'Country',
  platform: 'Platform',
  attack_id: 'ATT&CK ID',
  title: 'Title',
  date: 'Date',
  organization: 'Org',
  alt_names_count: 'Alt Names',
  description: 'Description',
};

function renderTooltipProps(type: string, props?: Record<string, unknown>): React.ReactNode[] {
  if (!props) return [];
  const items: React.ReactNode[] = [];
  const keys = type === 'CVE'
    ? ['cvss_score', 'cvss_severity', 'published_date', 'in_cisa_kev']
    : type === 'IOC'
    ? ['ioc_type', 'infrastructure']
    : type === 'ThreatActor'
    ? ['country']
    : type === 'MalwareFamily'
    ? ['platform']
    : type === 'AttackTechnique'
    ? ['attack_id']
    : type === 'Document'
    ? ['title', 'date', 'organization']
    : [];
  for (const k of keys) {
    const v = props[k];
    if (v == null || v === '' || v === false) continue;
    const label = PROP_LABELS[k] || k;
    let display = String(v);
    if (k === 'in_cisa_kev' && v === true) display = 'Yes';
    if (k === 'cvss_score' && props.cvss_severity) display = `${v} (${props.cvss_severity})`;
    if (k === 'cvss_severity') continue; // shown inline with score
    if (k === 'title' && display.length > 60) display = display.slice(0, 60) + '...';
    items.push(
      <div key={k} style={{ opacity: 0.7, fontSize: '11px' }}>{label}: {display}</div>
    );
  }
  return items;
}

// ── VT-compatible IOC sub-types ─────────────────────────────────────────────

const VT_TYPES = new Set(['IP', 'IPV6', 'DOMAIN', 'SHA256', 'SHA1', 'MD5', 'SHA512', 'URL']);

const QUICK_SEEDS: { name: string; label: string }[] = [
  { name: 'APT28', label: 'ThreatActor' },
  { name: 'Emotet', label: 'MalwareFamily' },
  { name: 'Cobalt Strike', label: 'MalwareFamily' },
  { name: 'CVE-2021-44228', label: 'CVE' },
  { name: 'Lazarus Group', label: 'ThreatActor' },
  { name: 'TrickBot', label: 'MalwareFamily' },
  { name: 'QakBot', label: 'MalwareFamily' },
  { name: 'APT29', label: 'ThreatActor' },
];

interface TooltipState {
  x: number;
  y: number;
  name: string;
  type: string;
  strategy?: string;
  score?: number;
  neighborCount?: number;
  neighborSummary?: string;
  properties?: Record<string, unknown>;
}

function emptyGraph(): AccumulatedGraph {
  return { nodes: [], edges: [], queryCount: 0, lastUpdated: Date.now() };
}

/** Count nodes by label for badge display. */
function countByLabel(graph: AccumulatedGraph): { label: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const n of graph.nodes) {
    counts.set(n.label, (counts.get(n.label) || 0) + 1);
  }
  return Array.from(counts.entries())
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count);
}

/** Build neighbor summary string for tooltip. */
function buildNeighborSummary(nodeId: string, graph: AccumulatedGraph) {
  const labelCounts: Record<string, number> = {};
  for (const e of graph.edges) {
    const neighborId = e.source === nodeId ? e.target : e.target === nodeId ? e.source : null;
    if (!neighborId) continue;
    const neighbor = graph.nodes.find(n => n.id === neighborId);
    if (neighbor) {
      labelCounts[neighbor.label] = (labelCounts[neighbor.label] || 0) + 1;
    }
  }
  const neighborCount = Object.values(labelCounts).reduce((a, b) => a + b, 0);
  const SHORT: Record<string, string> = {
    Document: 'doc', MalwareFamily: 'family', ThreatActor: 'actor',
    IOC: 'IOC', CVE: 'CVE', AttackTechnique: 'technique', WebSource: 'web',
  };
  const neighborSummary = Object.entries(labelCounts)
    .sort((a, b) => b[1] - a[1])
    .map(([lbl, cnt]) => `${cnt} ${SHORT[lbl] || lbl}${cnt > 1 ? 's' : ''}`)
    .join(', ');
  return { neighborCount, neighborSummary };
}

// ── Expand-only type options ────────────────────────────────────────────────

const EXPAND_TYPE_OPTIONS = [
  { label: 'IOC', display: 'Only IOCs' },
  { label: 'CVE', display: 'Only CVEs' },
  { label: 'MalwareFamily', display: 'Only Families' },
  { label: 'ThreatActor', display: 'Only Actors' },
  { label: 'AttackTechnique', display: 'Only Techniques' },
  { label: 'Document', display: 'Only Documents' },
];

// ── Graph context menu (portal-rendered) ────────────────────────────────────

function GraphContextMenuInner({
  node,
  position,
  isPinned,
  hasExpandedChildren,
  vtUrl,
  nvdUrl,
  mitreUrl,
  onExpand,
  onExpandDeep,
  onExpandOnly,
  onCollapse,
  onTogglePin,
  onFocus,
  onRemove,
  onHideType,
  onViewDetails,
  onFindPathFrom,
  onClose,
}: {
  node: AccumulatedNode;
  position: { x: number; y: number };
  isPinned: boolean;
  hasExpandedChildren: boolean;
  vtUrl: string | null;
  nvdUrl: string | null;
  mitreUrl: string | null;
  onExpand: () => void;
  onExpandDeep: () => void;
  onExpandOnly: (label: string) => void;
  onCollapse: () => void;
  onTogglePin: () => void;
  onFocus: () => void;
  onRemove: () => void;
  onHideType: (label: string) => void;
  onViewDetails?: () => void;
  onFindPathFrom: () => void;
  onClose: () => void;
}) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [showExpandTypes, setShowExpandTypes] = useState(false);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const style: React.CSSProperties = {
    position: 'fixed',
    top: Math.min(position.y, window.innerHeight - 400),
    left: Math.min(position.x, window.innerWidth - 200),
    zIndex: 1000,
  };

  return (
    <div ref={menuRef} className="malpedia-context-menu" style={style} role="menu">
      <button className="malpedia-context-menu-item" role="menuitem" onClick={onExpand}>
        Expand All
      </button>
      <button className="malpedia-context-menu-item" role="menuitem" onClick={onExpandDeep}>
        Expand 2 Hops
      </button>
      <div className="malpedia-context-menu-submenu-trigger"
        onMouseEnter={() => setShowExpandTypes(true)}
        onMouseLeave={() => setShowExpandTypes(false)}>
        <button className="malpedia-context-menu-item" role="menuitem">
          Expand only...
          <span className="malpedia-context-menu-arrow">{'\u25B6'}</span>
        </button>
        {showExpandTypes && (
          <div className="malpedia-context-menu malpedia-context-menu--submenu">
            {EXPAND_TYPE_OPTIONS.map(({ label, display }) => (
              <button key={label} className="malpedia-context-menu-item" role="menuitem"
                onClick={() => onExpandOnly(label)}>
                {display}
              </button>
            ))}
          </div>
        )}
      </div>
      {hasExpandedChildren && (
        <button className="malpedia-context-menu-item" role="menuitem" onClick={onCollapse}>
          Collapse
        </button>
      )}

      <div className="malpedia-context-menu-divider" />

      <button className="malpedia-context-menu-item" role="menuitem" onClick={onTogglePin}>
        {isPinned ? 'Unpin' : 'Pin'}
      </button>
      <button className="malpedia-context-menu-item" role="menuitem" onClick={onFocus}>
        Focus
      </button>
      <button className="malpedia-context-menu-item" role="menuitem"
        onClick={() => { onFindPathFrom(); onClose(); }}>
        Find Path To...
      </button>
      <button className="malpedia-context-menu-item" role="menuitem"
        onClick={() => onHideType(node.label)}>
        Hide all {node.label}
      </button>
      <button className="malpedia-context-menu-item malpedia-context-menu-item--danger" role="menuitem"
        onClick={onRemove}>
        Remove Node
      </button>

      {(onViewDetails || vtUrl || nvdUrl || mitreUrl) && (
        <div className="malpedia-context-menu-divider" />
      )}

      {onViewDetails && (
        <button className="malpedia-context-menu-item" role="menuitem" onClick={onViewDetails}>
          View Details
        </button>
      )}
      {vtUrl && (
        <button className="malpedia-context-menu-item malpedia-context-menu-item--external" role="menuitem"
          onClick={() => { window.open(vtUrl, '_blank', 'noopener'); onClose(); }}>
          Open in VirusTotal
        </button>
      )}
      {nvdUrl && (
        <button className="malpedia-context-menu-item malpedia-context-menu-item--external" role="menuitem"
          onClick={() => { window.open(nvdUrl, '_blank', 'noopener'); onClose(); }}>
          Open in NVD
        </button>
      )}
      {mitreUrl && (
        <button className="malpedia-context-menu-item malpedia-context-menu-item--external" role="menuitem"
          onClick={() => { window.open(mitreUrl, '_blank', 'noopener'); onClose(); }}>
          Open in MITRE ATT&CK
        </button>
      )}

      <div className="malpedia-context-menu-divider" />

      <button className="malpedia-context-menu-item" role="menuitem"
        onClick={() => { void navigator.clipboard.writeText(node.name); toast.success('Copied'); onClose(); }}>
        Copy Name
      </button>
    </div>
  );
}

// ── Path filters ───────────────────────────────────────────────────────────

export interface PathFilters {
  relTypes: string[];   // empty = all defaults
  dateFrom: string;
  dateTo: string;
}

export const PATH_REL_TYPES = [
  { value: 'USES', display: 'Uses' },
  { value: 'IDENTIFIES', display: 'Identifies' },
  { value: 'REFERENCED_IN', display: 'Referenced In' },
  { value: 'MENTIONS', display: 'Mentions' },
  { value: 'SAME_AS', display: 'Same As' },
  { value: 'USES_TECHNIQUE', display: 'Uses Technique' },
  { value: 'REFERENCES_TECHNIQUE', display: 'Refs Technique' },
  { value: 'SUBTECHNIQUE_OF', display: 'Sub-technique' },
  { value: 'MITIGATES', display: 'Mitigates' },
  { value: 'AFFECTS', display: 'Affects' },
  { value: 'CLASSIFIED_AS', display: 'Classified As' },
  { value: 'PART_OF', display: 'Part Of' },
];

// ── Path search popover ────────────────────────────────────────────────────

const PATH_CATEGORY_TO_LABEL: Record<string, string> = {
  actor: 'ThreatActor',
  family: 'MalwareFamily',
  ioc: 'IOC',
  cve: 'CVE',
  technique: 'AttackTechnique',
};

const PATH_CATEGORY_DISPLAY: Record<string, string> = {
  actor: 'Actor', family: 'Family', ioc: 'IOC', cve: 'CVE', technique: 'Technique',
};

function GraphPathSearchPopover({
  sourceNode,
  position,
  loading: pathLoading,
  onSelect,
  onClose,
}: {
  sourceNode: AccumulatedNode;
  position: { x: number; y: number };
  loading: boolean;
  onSelect: (name: string, label: string, filters: PathFilters) => void;
  onClose: () => void;
}) {
  const popoverRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ExploreSearchResult[]>([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [relTypes, setRelTypes] = useState<string[]>([]);

  // Auto-focus
  useEffect(() => { inputRef.current?.focus(); }, []);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const doSearch = useCallback(async (q: string) => {
    if (q.length < 2) { setResults([]); setOpen(false); return; }
    setSearching(true);
    try {
      const resp = await searchEntities({ query: q, limit: 10 });
      setResults(resp.results);
      setOpen(resp.results.length > 0);
      setActiveIndex(-1);
    } catch { setResults([]); }
    finally { setSearching(false); }
  }, []);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(val), 250);
  }, [doSearch]);

  const handleSelect = useCallback((result: ExploreSearchResult) => {
    const label = PATH_CATEGORY_TO_LABEL[result.category] || 'MalwareFamily';
    onSelect(result.value, label, { relTypes, dateFrom, dateTo });
  }, [onSelect, relTypes, dateFrom, dateTo]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!open || results.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(i => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeIndex >= 0 && activeIndex < results.length) {
        handleSelect(results[activeIndex]);
      }
    }
  }, [open, results, activeIndex, handleSelect]);

  const toggleRelType = useCallback((rt: string) => {
    setRelTypes(prev => prev.includes(rt) ? prev.filter(t => t !== rt) : [...prev, rt]);
  }, []);

  const style: React.CSSProperties = {
    position: 'fixed',
    top: Math.min(position.y, window.innerHeight - (showFilters ? 580 : 340)),
    left: Math.min(position.x, window.innerWidth - 300),
    zIndex: 1001,
  };

  return (
    <div ref={popoverRef} className="graph-path-search-popover" style={style}>
      <div className="graph-path-search-header">
        Find paths from <strong>{sourceNode.name}</strong>
      </div>
      <div className="graph-path-search-input-wrap">
        <input
          ref={inputRef}
          type="text"
          className="graph-path-search-input"
          placeholder="Search target entity..."
          value={query}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          disabled={pathLoading}
        />
        {(searching || pathLoading) && <span className="graph-search-bar-spinner" />}
      </div>
      {open && results.length > 0 && (
        <ul className="graph-path-search-dropdown">
          {results.map((r, i) => (
            <li
              key={`${r.category}-${r.value}`}
              className={`graph-path-search-item${i === activeIndex ? ' active' : ''}`}
              onMouseDown={() => handleSelect(r)}
              onMouseEnter={() => setActiveIndex(i)}
            >
              <span className={`graph-search-bar-cat-badge graph-search-bar-cat-${r.category}`}>
                {PATH_CATEGORY_DISPLAY[r.category] || r.category}
              </span>
              <span className="graph-path-search-item-name">{r.value}</span>
            </li>
          ))}
        </ul>
      )}
      <div className="graph-path-filter-toggle">
        <button
          className="graph-path-filter-toggle-btn"
          onClick={() => setShowFilters(v => !v)}
        >
          Filters {showFilters ? '▾' : '▸'}
        </button>
      </div>
      {showFilters && (
        <div className="graph-path-filter-section">
          <div className="expansion-settings-section">
            <div className="expansion-settings-label">Date range</div>
            <div className="expansion-settings-date-row">
              <input
                type="date"
                className="expansion-settings-date-input"
                value={dateFrom}
                onChange={e => setDateFrom(e.target.value)}
              />
              <span className="expansion-settings-date-sep">–</span>
              <input
                type="date"
                className="expansion-settings-date-input"
                value={dateTo}
                onChange={e => setDateTo(e.target.value)}
              />
            </div>
          </div>
          <div className="expansion-settings-section">
            <div className="expansion-settings-label">
              Relationship types
              <button
                className="expansion-settings-all-btn"
                onClick={() => setRelTypes(prev => prev.length === PATH_REL_TYPES.length ? [] : PATH_REL_TYPES.map(t => t.value))}
              >
                {relTypes.length === PATH_REL_TYPES.length ? 'Reset' : 'All'}
              </button>
            </div>
            <div className="expansion-settings-type-grid" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
              {PATH_REL_TYPES.map(rt => (
                <label key={rt.value} className="expansion-settings-checkbox-label">
                  <input
                    type="checkbox"
                    checked={relTypes.includes(rt.value)}
                    onChange={() => toggleRelType(rt.value)}
                  />
                  {rt.display}
                </label>
              ))}
            </div>
          </div>
        </div>
      )}
      <div className="graph-path-search-footer">
        <button className="graph-path-search-cancel" onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}

export default function GraphExplorerPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const wrapperRef = useRef<HTMLDivElement>(null);

  const [graph, setGraph] = useState<AccumulatedGraph>(emptyGraph());
  const [selectedNode, setSelectedNode] = useState<AccumulatedNode | null>(null);
  const [typeFilters, setTypeFilters] = useState<Record<string, boolean>>({});
  const [iocSubTypeFilters, setIocSubTypeFilters] = useState<Record<IOCType, boolean>>({
    HASH: true, IP: true, DOMAIN: true, URL: true, EMAIL: true, UNKNOWN: true,
  });
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [edgeColorMode, setEdgeColorMode] = useState<EdgeColorMode>('source');
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [docBadge, setDocBadge] = useState<{ name: string; label: string; docCount: number; el: HTMLElement } | null>(null);
  const [truncationWarning, setTruncationWarning] = useState<string | null>(null);
  const loadedSeedRef = useRef<string | null>(null);
  const loadedSavedIdRef = useRef<string | null>(null);

  // Undo stack (capped at 50 snapshots)
  const [undoStack, setUndoStack] = useState<AccumulatedGraph[]>([]);
  const hasUnsavedChanges = useRef(false);

  // Context menu state
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; nodeId: string } | null>(null);

  // Path-finding state
  const [pathPopover, setPathPopover] = useState<{ sourceNode: AccumulatedNode; x: number; y: number } | null>(null);
  const [pathResult, setPathResult] = useState<FindPathsResult | null>(null);
  const [pathLoading, setPathLoading] = useState(false);
  const [highlightedPathIndex, setHighlightedPathIndex] = useState<number | null>(null);
  const [landingMode, setLandingMode] = useState<'explore' | 'paths'>('explore');

  // Expansion settings
  const [expansionSettings, setExpansionSettings] = useState<ExpansionSettings>(loadExpansionSettings);

  // Resizable sidebar
  const { width: sidebarWidth, isDragging: isSidebarDragging, handleMouseDown: handleSidebarDragStart } = useResizablePanel();

  const updateExpansionSettings = useCallback((patch: Partial<ExpansionSettings>) => {
    setExpansionSettings(prev => {
      const next = { ...prev, ...patch };
      localStorage.setItem('graph-expansion-settings', JSON.stringify(next));
      return next;
    });
  }, []);

  // Container dimensions
  const [dimensions, setDimensions] = useState({ width: 800, height: 600 });

  // Title editing state
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const titleInputRef = useRef<HTMLInputElement>(null);

  // Graph storage
  const {
    savedGraphs,
    activeGraphId,
    saveGraph,
    renameGraph,
    updateGraphData,
    setActiveGraphId,
  } = useGraphStorageContext();

  // Current seed entity for title derivation
  const [currentSeedEntity, setCurrentSeedEntity] = useState<string | null>(null);
  const [currentSeedLabel, setCurrentSeedLabel] = useState<string | null>(null);

  // Active saved graph object
  const activeSavedGraph: SavedGraph | undefined = activeGraphId
    ? savedGraphs.find((g) => g.id === activeGraphId)
    : undefined;

  // ── Undo / unsaved-changes helpers ────────────────────────────────────────

  const pushUndo = useCallback((snapshot: AccumulatedGraph) => {
    setUndoStack(prev => [...prev.slice(-(50 - 1)), snapshot]);
  }, []);

  const handleUndo = useCallback(() => {
    setUndoStack(prev => {
      if (prev.length === 0) return prev;
      const next = prev.slice(0, -1);
      setGraph(prev[prev.length - 1]);
      return next;
    });
  }, []);

  // Track unsaved changes whenever graph.lastUpdated changes
  useEffect(() => {
    if (graph.nodes.length > 0) {
      hasUnsavedChanges.current = true;
    }
  }, [graph.lastUpdated]);

  // ── Seed / expand / smart view callbacks ─────────────────────────────────

  const handleSeed = useCallback(async (name: string, label: string) => {
    setLoading(true);
    setError(null);
    setTruncationWarning(null);
    try {
      const result = await seedGraph(
        name, label, 30,
        expansionSettings.dateFrom || undefined,
        expansionSettings.dateTo || undefined,
      );
      if (result.nodes.length === 0) {
        setError(`No entity found for "${name}"`);
        setGraph(emptyGraph());
        return;
      }
      // Check for document truncation
      const seedNode = result.nodes[0];
      const filteredTotal = seedNode?.score ?? 0;       // doc count within date range (or total if no filter)
      const totalDocs = (seedNode?.properties as Record<string, unknown>)?.total_doc_count as number | undefined;
      const returnedDocs = result.nodes.filter(n => n.label === 'Document').length;
      if (filteredTotal > returnedDocs && returnedDocs > 0) {
        const ofTotal = totalDocs != null ? ` (${totalDocs} total)` : '';
        setTruncationWarning(
          `Showing ${returnedDocs} of ${Math.round(filteredTotal)}${ofTotal} documents for ${name}. Use date filters to narrow results.`
        );
      }
      const ts = Date.now();
      const nodes: AccumulatedNode[] = result.nodes.map(n => ({
        id: n.id,
        label: n.label,
        name: n.name,
        score: n.score,
        strategy: n.strategy,
        properties: n.properties,
        addedAtQueryIndex: 0,
        lastSeenAtQueryIndex: 0,
        addedAtTimestamp: ts,
        sourceQueryIds: [`seed:${name}`],
        pinned: false,
      }));
      const edges: AccumulatedEdge[] = result.edges.map(e => ({
        id: `${e.source}--${e.rel_type}--${e.target}`,
        source: e.source,
        target: e.target,
        rel_type: e.rel_type,
        addedAtQueryIndex: 0,
        sourceQueryIds: [`seed:${name}`],
      }));
      pushUndo(graph);
      setGraph({ nodes, edges, queryCount: 1, lastUpdated: ts });
      setSelectedNode(null);
      setCurrentSeedEntity(name);
      setCurrentSeedLabel(label);
      setActiveGraphId(null);
      loadedSavedIdRef.current = null;
      loadedSeedRef.current = name;
      setSearchParams({ seed: name, label }, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Seed failed');
    } finally {
      setLoading(false);
    }
  }, [setSearchParams, setActiveGraphId, expansionSettings.dateFrom, expansionSettings.dateTo, graph, pushUndo]);

  // Load from URL params on mount / searchParams change
  useEffect(() => {
    const savedId = searchParams.get('savedGraphId');
    if (savedId && savedId !== loadedSavedIdRef.current) {
      const found = savedGraphs.find((g) => g.id === savedId);
      if (found) {
        loadedSavedIdRef.current = savedId;
        loadedSeedRef.current = found.seedEntity ?? null;
        setGraph(found.graph);
        setActiveGraphId(savedId);
        setCurrentSeedEntity(found.seedEntity ?? null);
        setCurrentSeedLabel(found.seedLabel ?? null);
        setSelectedNode(null);
        setError(null);
        hasUnsavedChanges.current = false;
        setUndoStack([]);
        return;
      }
    }
    const seed = searchParams.get('seed');
    const label = searchParams.get('label') || 'MalwareFamily';
    if (seed && seed !== loadedSeedRef.current) {
      loadedSeedRef.current = seed;
      handleSeed(seed, label);
      return;
    }
    // Bare /graph with no params → reset to landing page
    if (!savedId && !seed && (loadedSavedIdRef.current || loadedSeedRef.current)) {
      loadedSavedIdRef.current = null;
      loadedSeedRef.current = null;
      setGraph(emptyGraph());
      setActiveGraphId(null);
      setCurrentSeedEntity(null);
      setCurrentSeedLabel(null);
      setSelectedNode(null);
      setError(null);
    }
  }, [searchParams, handleSeed, savedGraphs, setActiveGraphId]);

  const handleExpand = useCallback(async (nodeId: string, overrideDepth?: number) => {
    const accNode = graph.nodes.find(n => n.id === nodeId);
    if (!accNode) return;
    const depth = overrideDepth ?? expansionSettings.depth;
    const excludeLabels = expansionSettings.excludeDocuments ? ['Document'] : [];
    try {
      const resp = await expandNeighbors(
        nodeId, accNode.label, accNode.name,
        expansionSettings.maxNeighbors,
        expansionSettings.excludeDocuments,
        depth,
        excludeLabels,
        expansionSettings.includeLabels,
        expansionSettings.dateFrom || undefined,
        expansionSettings.dateTo || undefined,
      );
      const ts = Date.now();
      const nodeMap = new Map(graph.nodes.map(n => [n.id, n]));
      const edgeMap = new Map(graph.edges.map(e => [e.id, e]));

      for (const n of resp.nodes) {
        const existing = nodeMap.get(n.id);
        if (existing) {
          // Enrich existing node with new properties
          if (n.properties) {
            existing.properties = { ...existing.properties, ...n.properties };
          }
        } else {
          nodeMap.set(n.id, {
            id: n.id, label: n.label, name: n.name, score: n.score, strategy: n.strategy,
            properties: n.properties,
            addedAtQueryIndex: graph.queryCount, lastSeenAtQueryIndex: graph.queryCount,
            addedAtTimestamp: ts, sourceQueryIds: [`expand:${nodeId}`], pinned: false,
          });
        }
      }
      for (const e of resp.edges) {
        const key = `${e.source}--${e.rel_type}--${e.target}`;
        if (!edgeMap.has(key)) {
          edgeMap.set(key, {
            id: key, source: e.source, target: e.target, rel_type: e.rel_type,
            addedAtQueryIndex: graph.queryCount, sourceQueryIds: [`expand:${nodeId}`],
            expanded: true,
          });
        }
      }

      const newGraph: AccumulatedGraph = {
        nodes: Array.from(nodeMap.values()),
        edges: Array.from(edgeMap.values()),
        queryCount: graph.queryCount,
        lastUpdated: ts,
      };
      pushUndo(graph);
      setGraph(newGraph);
    } catch (err) {
      console.warn('Neighbor expansion failed:', err);
    }
  }, [graph, expansionSettings, pushUndo]);

  // ── Context menu action handlers ──────────────────────────────────────────

  const handleRemoveNode = useCallback((nodeId: string) => {
    pushUndo(graph);
    setGraph(prev => ({
      ...prev,
      nodes: prev.nodes.filter(n => n.id !== nodeId),
      edges: prev.edges.filter(e => e.source !== nodeId && e.target !== nodeId),
      lastUpdated: Date.now(),
    }));
    if (selectedNode?.id === nodeId) setSelectedNode(null);
    setContextMenu(null);
  }, [selectedNode, graph, pushUndo]);

  const handleCollapse = useCallback((nodeId: string) => {
    // Remove nodes that were added by expanding this node
    pushUndo(graph);
    const expandTag = `expand:${nodeId}`;
    setGraph(prev => {
      const removeIds = new Set(
        prev.nodes
          .filter(n => n.id !== nodeId && n.sourceQueryIds.includes(expandTag))
          .map(n => n.id),
      );
      if (removeIds.size === 0) return prev;
      return {
        ...prev,
        nodes: prev.nodes.filter(n => !removeIds.has(n.id)),
        edges: prev.edges.filter(e => !removeIds.has(e.source) && !removeIds.has(e.target)),
        lastUpdated: Date.now(),
      };
    });
    setContextMenu(null);
  }, [graph, pushUndo]);

  const handleExpandOnly = useCallback(async (nodeId: string, targetLabel: string) => {
    const accNode = graph.nodes.find(n => n.id === nodeId);
    if (!accNode) return;
    try {
      const resp = await expandNeighbors(
        nodeId, accNode.label, accNode.name,
        expansionSettings.maxNeighbors,
        targetLabel === 'Document' ? false : expansionSettings.excludeDocuments,
        expansionSettings.depth,
        expansionSettings.excludeDocuments && targetLabel !== 'Document' ? ['Document'] : [],
        [targetLabel],
        expansionSettings.dateFrom || undefined,
        expansionSettings.dateTo || undefined,
      );
      const ts = Date.now();
      const nodeMap = new Map(graph.nodes.map(n => [n.id, n]));
      const edgeMap = new Map(graph.edges.map(e => [e.id, e]));
      for (const n of resp.nodes) {
        const existing = nodeMap.get(n.id);
        if (existing) {
          if (n.properties) existing.properties = { ...existing.properties, ...n.properties };
        } else {
          nodeMap.set(n.id, {
            id: n.id, label: n.label, name: n.name, score: n.score, strategy: n.strategy,
            properties: n.properties,
            addedAtQueryIndex: graph.queryCount, lastSeenAtQueryIndex: graph.queryCount,
            addedAtTimestamp: ts, sourceQueryIds: [`expand:${nodeId}`], pinned: false,
          });
        }
      }
      for (const e of resp.edges) {
        const key = `${e.source}--${e.rel_type}--${e.target}`;
        if (!edgeMap.has(key)) {
          edgeMap.set(key, {
            id: key, source: e.source, target: e.target, rel_type: e.rel_type,
            addedAtQueryIndex: graph.queryCount, sourceQueryIds: [`expand:${nodeId}`],
            expanded: true,
          });
        }
      }
      pushUndo(graph);
      setGraph({
        nodes: Array.from(nodeMap.values()),
        edges: Array.from(edgeMap.values()),
        queryCount: graph.queryCount,
        lastUpdated: ts,
      });
    } catch (err) {
      console.warn('Type-specific expansion failed:', err);
    }
    setContextMenu(null);
  }, [graph, expansionSettings, pushUndo]);

  // ── Path-finding handlers ─────────────────────────────────────────────────

  const handleFindPath = useCallback(async (
    sourceNode: AccumulatedNode,
    targetName: string,
    targetLabel: string,
    filters?: PathFilters,
  ) => {
    setPathLoading(true);
    setPathPopover(null);
    try {
      const result = await findPaths(
        sourceNode.id, sourceNode.label, sourceNode.name,
        '', targetLabel, targetName,
        5, 5, ['Chunk', 'ChunkRun'], true,
        filters?.relTypes?.length ? filters.relTypes : undefined,
        filters?.dateFrom || undefined,
        filters?.dateTo || undefined,
      );

      setPathResult(result);
      setHighlightedPathIndex(null);

      // Merge path subgraph nodes/edges into accumulated graph
      if (result.subgraph.nodes.length > 0) {
        const ts = Date.now();
        pushUndo(graph);
        setGraph(prev => {
          const nodeMap = new Map(prev.nodes.map(n => [n.id, n]));
          const edgeMap = new Map(prev.edges.map(e => [e.id, e]));

          for (const n of result.subgraph.nodes) {
            if (!nodeMap.has(n.id)) {
              nodeMap.set(n.id, {
                id: n.id, label: n.label, name: n.name,
                score: n.score, strategy: n.strategy,
                properties: n.properties,
                addedAtQueryIndex: prev.queryCount,
                lastSeenAtQueryIndex: prev.queryCount,
                addedAtTimestamp: ts,
                sourceQueryIds: [`path:${sourceNode.name}->${targetName}`],
                pinned: false,
              });
            }
          }
          for (const e of result.subgraph.edges) {
            const key = `${e.source}--${e.rel_type}--${e.target}`;
            if (!edgeMap.has(key)) {
              edgeMap.set(key, {
                id: key, source: e.source, target: e.target, rel_type: e.rel_type,
                addedAtQueryIndex: prev.queryCount,
                sourceQueryIds: [`path:${sourceNode.name}->${targetName}`],
              });
            }
          }

          return {
            nodes: Array.from(nodeMap.values()),
            edges: Array.from(edgeMap.values()),
            queryCount: prev.queryCount,
            lastUpdated: ts,
          };
        });

      }

      if (result.paths.length > 0) {
        toast.success(result.meta.message);
      } else {
        toast('No paths found between these nodes');
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Path finding failed');
    } finally {
      setPathLoading(false);
    }
  }, [graph, pushUndo]);

  // ── Landing page path search handler ────────────────────────────────────

  const handleLandingPathFound = useCallback((result: FindPathsResult) => {
    if (result.subgraph.nodes.length === 0) return;
    const ts = Date.now();
    const nodeMap = new Map<string, AccumulatedNode>();
    const edgeMap = new Map<string, AccumulatedEdge>();

    for (const n of result.subgraph.nodes) {
      nodeMap.set(n.id, {
        id: n.id, label: n.label, name: n.name,
        score: n.score, strategy: n.strategy,
        properties: n.properties,
        addedAtQueryIndex: 0,
        lastSeenAtQueryIndex: 0,
        addedAtTimestamp: ts,
        sourceQueryIds: ['landing-path'],
        pinned: false,
      });
    }
    for (const e of result.subgraph.edges) {
      const key = `${e.source}--${e.rel_type}--${e.target}`;
      edgeMap.set(key, {
        id: key, source: e.source, target: e.target, rel_type: e.rel_type,
        addedAtQueryIndex: 0,
        sourceQueryIds: ['landing-path'],
      });
    }

    pushUndo(graph);
    setGraph({
      nodes: Array.from(nodeMap.values()),
      edges: Array.from(edgeMap.values()),
      queryCount: 1,
      lastUpdated: ts,
    });
    setPathResult(result);
    setHighlightedPathIndex(null);
  }, [graph, pushUndo]);

  // Compute highlighted node/edge IDs for path highlighting
  const pathHighlight = useMemo(() => {
    if (!pathResult || pathResult.paths.length === 0) return null;
    const paths = highlightedPathIndex != null
      ? [pathResult.paths[highlightedPathIndex]]
      : pathResult.paths;

    const nodeIds = new Set<string>();
    const edgeKeys = new Set<string>();
    for (const p of paths) {
      for (const step of p.nodes) nodeIds.add(step.node_id);
      for (let i = 0; i < p.edges.length; i++) {
        const src = p.nodes[i].node_id;
        const tgt = p.nodes[i + 1].node_id;
        edgeKeys.add(`${src}--${p.edges[i]}--${tgt}`);
        edgeKeys.add(`${tgt}--${p.edges[i]}--${src}`);
      }
    }
    return { nodeIds, edgeKeys };
  }, [pathResult, highlightedPathIndex]);

  // ── Filter helpers ───────────────────────────────────────────────────────

  const handleToggleTypeFilter = useCallback((type: string) => {
    setTypeFilters(prev => ({ ...prev, [type]: prev[type] === false ? true : false }));
  }, []);

  const handleShowAll = useCallback(() => {
    const all: Record<string, boolean> = {};
    ALL_TYPES.forEach(t => { all[t] = true; });
    setTypeFilters(all);
    setIocSubTypeFilters({ HASH: true, IP: true, DOMAIN: true, URL: true, EMAIL: true, UNKNOWN: true });
  }, []);

  const handleToggleIocSubType = useCallback((type: IOCType) => {
    setIocSubTypeFilters(prev => ({ ...prev, [type]: prev[type] === false ? true : false }));
  }, []);

  const handleDateRangeChange = useCallback((from: string, to: string) => {
    setFilterDateFrom(from);
    setFilterDateTo(to);
    // Sync with expansion settings so future expands use the same range
    updateExpansionSettings({ dateFrom: from, dateTo: to });
  }, [updateExpansionSettings]);

  // ── Transform AccumulatedGraph → ForceGraph data ─────────────────────────

  const graphData = useMemo(() => {
    // Pass 1: type + IOC sub-type + date filtering
    let filteredNodes = graph.nodes.filter(n => {
      // Main type filter
      if (typeFilters[n.label] === false) return false;
      // IOC sub-type filter
      if (n.label === 'IOC' && iocSubTypeFilters[classifyIOC(n.name)] === false) return false;
      // Date filter: only applies to Document nodes
      if (n.label === 'Document' && (filterDateFrom || filterDateTo)) {
        const docDate = (n.properties?.date as string) || '';
        if (docDate) {
          if (filterDateFrom && docDate < filterDateFrom) return false;
          if (filterDateTo && docDate > filterDateTo) return false;
        } else {
          // Documents without a date are excluded when a date filter is active
          return false;
        }
      }
      return true;
    });

    // Pass 2: remove orphaned non-Document nodes that only connected to now-hidden Documents
    if (filterDateFrom || filterDateTo) {
      const visibleIds = new Set(filteredNodes.map(n => n.id));
      const connectedToVisible = new Set<string>();
      for (const e of graph.edges) {
        if (visibleIds.has(e.source) && visibleIds.has(e.target)) {
          connectedToVisible.add(e.source);
          connectedToVisible.add(e.target);
        }
      }
      // Keep nodes that: have visible connections, OR have no document edges at all, OR are seed (index 0)
      filteredNodes = filteredNodes.filter((n, i) => {
        if (i === 0) return true; // always keep seed
        if (n.label === 'Document') return true; // already filtered above
        // Check if this node has any edges at all in the full graph
        const hasAnyEdge = graph.edges.some(e => e.source === n.id || e.target === n.id);
        if (!hasAnyEdge) return true; // isolated node — keep
        // Keep if it still has at least one visible connection
        return connectedToVisible.has(n.id);
      });
    }

    const filteredNodeIds = new Set(filteredNodes.map(n => n.id));
    const filteredEdges = graph.edges.filter(e => filteredNodeIds.has(e.source) && filteredNodeIds.has(e.target));

    const nodes: FGNode[] = filteredNodes.map((n, i) => {
      const docCount = n.score ?? 0;
      const baseSize = Math.max(4, Math.min(8, 3 + Math.log2(docCount + 1) * 1.2));
      const nodeSize = i === 0 ? Math.max(baseSize, 8) : baseSize;
      let color = nodeColor(n.label, n.name);
      // Dim non-path nodes when path highlighting is active
      if (pathHighlight && !pathHighlight.nodeIds.has(n.id)) {
        // Blend 25% node color with 75% background (#0a0a0f) as hex
        let h = color.replace('#', '');
        if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
        const cr = parseInt(h.substring(0, 2), 16);
        const cg = parseInt(h.substring(2, 4), 16);
        const cb = parseInt(h.substring(4, 6), 16);
        const br = 10, bg = 10, bb = 15; // #0a0a0f
        const mr = Math.round(cr * 0.25 + br * 0.75);
        const mg = Math.round(cg * 0.25 + bg * 0.75);
        const mb = Math.round(cb * 0.25 + bb * 0.75);
        color = `#${mr.toString(16).padStart(2, '0')}${mg.toString(16).padStart(2, '0')}${mb.toString(16).padStart(2, '0')}`;
      }
      return {
        id: n.id,
        name: n.name,
        label: n.label,
        color,
        size: nodeSize,
        isSeed: i === 0,
        strategy: n.strategy,
        score: n.score,
        fx: n.position?.x,
        fy: n.position?.y,
      };
    });

    const links: FGLink[] = filteredEdges.map(e => {
      const ec = edgeColorMode === 'source'
        ? sourceEdgeColor(e.rel_type, e.data_source)
        : edgeColor(e.rel_type);
      const dimmed = pathHighlight && !pathHighlight.edgeKeys.has(`${e.source}--${e.rel_type}--${e.target}`);
      return {
        source: e.source,
        target: e.target,
        rel_type: e.rel_type,
        color: dimmed ? 'rgba(255,255,255,0.06)' : ec.line,
        particleColor: dimmed ? 'transparent' : ec.particle,
        expanded: e.expanded,
        data_source: e.data_source,
      };
    });

    return { nodes, links };
  }, [graph, typeFilters, iocSubTypeFilters, filterDateFrom, filterDateTo, pathHighlight, edgeColorMode]);

  // ── Force graph hook ─────────────────────────────────────────────────────

  const seedNodeId = graph.nodes.length > 0 ? graph.nodes[0].id : null;

  const {
    fgRef,
    hoveredNodeId,
    nodeCanvasObject,
    nodePointerAreaPaint,
    handleNodeHover,
    handleNodeUnhover,
    handleNodeClick,
    linkColor,
    linkWidth,
    linkParticleColor,
    linkLineDash,
    focusNode,
    zoomToFit,
  } = useForceGraph({
    onNodeClick: (node) => {
      setContextMenu(null);
      const accNode = graph.nodes.find(n => n.id === node.id);
      if (!accNode) return;
      setSelectedNode(accNode);
    },
    onNodeDoubleClick: (node) => {
      handleExpand(node.id as string);
    },
    onBackgroundClick: () => {
      setSelectedNode(null);
      setContextMenu(null);
    },
    highlightedNodeId: selectedNode?.id ?? null,
    seedNodeId,
  });

  // ── Position path nodes & zoom after path-finding ──────────────────────

  useEffect(() => {
    if (!pathResult || pathResult.paths.length === 0) return;
    const fg = fgRef.current;
    if (!fg) return;

    const allPathNodeIds = new Set(
      pathResult.paths.flatMap(p => p.nodes.map(n => n.node_id)),
    );

    // Find source node's current position in the force graph
    const sourceNodeId = pathResult.paths[0]?.nodes[0]?.node_id;
    const fgNodes = graphData.nodes;
    const sourceNode = fgNodes.find(n => n.id === sourceNodeId);
    const sourceX = sourceNode?.x ?? 0;
    const sourceY = sourceNode?.y ?? 0;

    // Position nodes across ALL paths, fanning vertically
    const positioned = new Set<string>();
    for (let pi = 0; pi < pathResult.paths.length; pi++) {
      const path = pathResult.paths[pi];
      const yOffset = pi * 80;
      for (let i = 0; i < path.nodes.length; i++) {
        const nid = path.nodes[i].node_id;
        if (positioned.has(nid)) continue;
        const fgNode = fgNodes.find(n => n.id === nid);
        if (
          fgNode &&
          (fgNode.x === 0 || fgNode.x == null) &&
          (fgNode.y === 0 || fgNode.y == null)
        ) {
          fgNode.x = sourceX + i * 100;
          fgNode.y = sourceY + yOffset + (Math.random() - 0.5) * 40;
          positioned.add(nid);
        }
      }
    }

    // Zoom to fit all path nodes after layout settles
    setTimeout(() => {
      fg.zoomToFit(400, 60, (node: FGNode) => allPathNodeIds.has(node.id as string));
    }, 100);
  }, [pathResult]);

  // ── Zoom-to-path on path item click ───────────────────────────────────

  const handlePathClick = useCallback((idx: number) => {
    const newIdx = highlightedPathIndex === idx ? null : idx;
    setHighlightedPathIndex(newIdx);

    const fg = fgRef.current;
    if (!fg || !pathResult) return;

    if (newIdx != null) {
      const pathNodeIds = new Set(pathResult.paths[newIdx].nodes.map(n => n.node_id));
      fg.zoomToFit(400, 60, (node: FGNode) => pathNodeIds.has(node.id as string));
    } else {
      const allPathNodeIds = new Set(
        pathResult.paths.flatMap(p => p.nodes.map(n => n.node_id)),
      );
      fg.zoomToFit(400, 60, (node: FGNode) => allPathNodeIds.has(node.id as string));
    }
  }, [highlightedPathIndex, pathResult]);

  // ── Pin toggle (needs fgRef + graphData) ────────────────────────────────

  const handleTogglePin = useCallback((nodeId: string) => {
    const fgNode = graphData.nodes.find(n => n.id === nodeId);
    if (!fgNode) return;
    const wasPinned = fgNode.fx != null;
    if (wasPinned) {
      fgNode.fx = undefined;
      fgNode.fy = undefined;
    } else {
      fgNode.fx = fgNode.x;
      fgNode.fy = fgNode.y;
    }
    // Persist pin state + position into AccumulatedNode
    pushUndo(graph);
    setGraph(prev => ({
      ...prev,
      nodes: prev.nodes.map(n =>
        n.id === nodeId
          ? {
              ...n,
              pinned: !wasPinned,
              position: !wasPinned ? { x: fgNode.x ?? 0, y: fgNode.y ?? 0 } : undefined,
            }
          : n,
      ),
      lastUpdated: Date.now(),
    }));
    fgRef.current?.d3ReheatSimulation();
    setContextMenu(null);
  }, [graphData.nodes, fgRef, graph, pushUndo]);

  // ── Tooltip from hover ───────────────────────────────────────────────────

  const onNodeHover = useCallback((node: FGNode | null, prev: FGNode | null) => {
    handleNodeHover(node, prev, graphData);

    if (!node) {
      setTooltip(null);
      return;
    }

    const fg = fgRef.current;
    if (!fg) return;
    const screenPos = fg.graph2ScreenCoords(node.x ?? 0, node.y ?? 0);

    const accNode = graph.nodes.find(n => n.id === node.id);
    const { neighborCount, neighborSummary } = buildNeighborSummary(node.id as string, graph);

    setTooltip({
      x: screenPos.x,
      y: screenPos.y,
      name: (node as GraphNode).name,
      type: (node as GraphNode).label,
      strategy: accNode?.strategy,
      score: accNode?.score,
      neighborCount,
      neighborSummary,
      properties: accNode?.properties,
    });
  }, [handleNodeHover, graphData, graph, fgRef]);

  const onLinkHover = useCallback((link: FGLink | null) => {
    if (!link) {
      setTooltip(null);
      return;
    }
    const fg = fgRef.current;
    if (!fg) return;
    const src = link.source as FGNode;
    const tgt = link.target as FGNode;
    if (!src?.x || !tgt?.x) return;
    const mx = ((src.x ?? 0) + (tgt.x ?? 0)) / 2;
    const my = ((src.y ?? 0) + (tgt.y ?? 0)) / 2;
    const screenPos = fg.graph2ScreenCoords(mx, my);
    const gl = link as GraphLink;
    const ks = resolveKnowledgeSource(gl.rel_type, gl.data_source);
    setTooltip({
      x: screenPos.x,
      y: screenPos.y,
      name: gl.rel_type,
      type: KNOWLEDGE_SOURCE_LABELS[ks],
    });
  }, [fgRef]);

  // ── Drag end: pin node ───────────────────────────────────────────────────

  const onNodeDragEnd = useCallback((node: FGNode) => {
    // Pin the node where it was dropped
    node.fx = node.x;
    node.fy = node.y;
  }, []);

  // ── Container resize tracking ────────────────────────────────────────────

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const obs = new ResizeObserver(entries => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        if (width > 0 && height > 0) {
          setDimensions({ width, height });
        }
      }
    });
    obs.observe(wrapper);
    return () => obs.disconnect();
  }, [graph.nodes.length === 0]);

  // Zoom to fit when graph data changes
  useEffect(() => {
    if (graphData.nodes.length > 0) {
      // Delay to let the simulation settle a bit
      const t = setTimeout(() => zoomToFit(50, 600), 300);
      return () => clearTimeout(t);
    }
  }, [graph.lastUpdated]);

  // ── Configure d3-force after mount ───────────────────────────────────────

  useEffect(() => {
    const fg = fgRef.current;
    if (!fg) return;
    const charge = fg.d3Force('charge');
    if (charge && typeof (charge as any).strength === 'function') {
      (charge as any).strength(-120);
    }
    const link = fg.d3Force('link');
    if (link && typeof (link as any).distance === 'function') {
      (link as any).distance(60);
    }
  });

  // ── Focus node handler (for NodeDetailsPanel) ────────────────────────────

  const handleFocusNode = useCallback((nodeId: string) => {
    focusNode(nodeId, graphData);
    const accNode = graph.nodes.find(n => n.id === nodeId);
    if (accNode) setSelectedNode(accNode);
  }, [focusNode, graphData, graph.nodes]);

  const handleResetLayout = useCallback(() => {
    // Unpin all nodes, reheat simulation
    for (const node of graphData.nodes) {
      node.fx = undefined;
      node.fy = undefined;
    }
    fgRef.current?.d3ReheatSimulation();
    setTimeout(() => zoomToFit(50, 600), 500);
  }, [graphData.nodes, fgRef, zoomToFit]);

  // ── Save / title handlers ────────────────────────────────────────────────

  const handleSaveGraph = useCallback(() => {
    if (activeGraphId) {
      updateGraphData(activeGraphId, graph);
    } else {
      const title = currentSeedEntity || graph.nodes[0]?.name || 'Untitled graph';
      const id = saveGraph(graph, title, currentSeedEntity ?? undefined, currentSeedLabel ?? undefined);
      setSearchParams({ savedGraphId: id }, { replace: true });
      loadedSavedIdRef.current = id;
    }
    hasUnsavedChanges.current = false;
  }, [graph, activeGraphId, currentSeedEntity, currentSeedLabel, saveGraph, updateGraphData, setSearchParams]);

  const handleStartTitleEdit = useCallback(() => {
    if (!activeSavedGraph) return;
    setTitleDraft(activeSavedGraph.title);
    setEditingTitle(true);
    setTimeout(() => titleInputRef.current?.select(), 0);
  }, [activeSavedGraph]);

  const handleCommitTitle = useCallback(() => {
    if (activeGraphId && titleDraft.trim()) {
      renameGraph(activeGraphId, titleDraft.trim());
    }
    setEditingTitle(false);
  }, [activeGraphId, titleDraft, renameGraph]);

  const handleLoadSavedGraph = useCallback((id: string) => {
    setSearchParams({ savedGraphId: id }, { replace: true });
  }, [setSearchParams]);

  // ── Graph-to-prompt handlers ─────────────────────────────────────────────

  const handleCopyGraphToClipboard = useCallback(() => {
    const visibleNodes = graphData.nodes;
    const visibleNodeIds = new Set(visibleNodes.map(n => n.id));
    const accNodes = graph.nodes.filter(n => visibleNodeIds.has(n.id));
    const accEdges = graph.edges.filter(e => visibleNodeIds.has(e.source) && visibleNodeIds.has(e.target));
    const text = serializeGraphToPrompt(accNodes, accEdges, {
      title: currentSeedEntity || 'Graph Investigation',
    });
    navigator.clipboard.writeText(text);
    toast.success('Graph context copied to clipboard');
  }, [graphData.nodes, graph, currentSeedEntity]);

  const handleInsertGraphIntoChat = useCallback(() => {
    const visibleNodes = graphData.nodes;
    const visibleNodeIds = new Set(visibleNodes.map(n => n.id));
    const accNodes = graph.nodes.filter(n => visibleNodeIds.has(n.id));
    const accEdges = graph.edges.filter(e => visibleNodeIds.has(e.source) && visibleNodeIds.has(e.target));
    const text = serializeGraphToPrompt(accNodes, accEdges, {
      title: currentSeedEntity || 'Graph Investigation',
    });
    sessionStorage.setItem('malpedia-pending-graph-context', text);
    navigate('/');
    toast.success('Graph context added to chat');
  }, [graphData.nodes, graph, currentSeedEntity, navigate]);

  const isEmpty = graph.nodes.length === 0;
  const detailMessages: { id: string; role: string; content: string }[] = [];

  const recentGraphs = [...savedGraphs]
    .sort((a, b) => b.updatedAt - a.updatedAt)
    .slice(0, 6);

  return (
    <div className="malpedia-graph-page graph-explorer-page">
      {/* Top bar */}
      <div className="graph-explorer-topbar">
        <GraphSearchBar onSeed={handleSeed} autoFocus={isEmpty && !loading} />

        {!isEmpty && (
          <>
            {activeSavedGraph && (
              <div className="graph-explorer-title-display">
                {editingTitle ? (
                  <input
                    ref={titleInputRef}
                    className="graph-explorer-title-input"
                    value={titleDraft}
                    onChange={(e) => setTitleDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleCommitTitle();
                      if (e.key === 'Escape') setEditingTitle(false);
                    }}
                    onBlur={handleCommitTitle}
                  />
                ) : (
                  <span
                    className="graph-explorer-title-text"
                    onClick={handleStartTitleEdit}
                    title="Click to rename"
                  >
                    {activeSavedGraph.title}
                  </span>
                )}
              </div>
            )}
            <button className={`graph-explorer-save-btn${hasUnsavedChanges.current ? ' has-unsaved' : ''}`} onClick={handleSaveGraph} title="Save this graph">
              Save{hasUnsavedChanges.current && <span className="graph-explorer-unsaved-dot" />}
            </button>
          </>
        )}

        {!isEmpty && (
          <div className="graph-explorer-topbar-actions">
            <button
              className="graph-explorer-toolbar-btn"
              onClick={handleUndo}
              disabled={undoStack.length === 0}
              title={undoStack.length > 0 ? `Undo (${undoStack.length})` : 'Nothing to undo'}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 7h7a4 4 0 010 8H7" />
                <path d="M6 4L3 7l3 3" />
              </svg>
              Undo
            </button>
            <button className="graph-explorer-toolbar-btn" onClick={handleResetLayout}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                <path d="M2 8a6 6 0 0111.3-2.8M14 8a6 6 0 01-11.3 2.8" />
                <path d="M14 2v4h-4M2 14v-4h4" />
              </svg>
              Reset Layout
            </button>
            <button className="graph-explorer-toolbar-btn" onClick={handleCopyGraphToClipboard} title="Copy graph context as markdown">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="5" y="2" width="8" height="10" rx="1" />
                <path d="M3 6v7a1 1 0 001 1h7" />
              </svg>
              Copy
            </button>
            <button className="graph-explorer-toolbar-btn" onClick={handleInsertGraphIntoChat} title="Insert graph context into chat">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 8H6M10 4l4 4-4 4" />
                <path d="M2 3v10" />
              </svg>
              Prompt
            </button>
            <span className="graph-explorer-toolbar-badge">
              {graph.nodes.length} nodes &middot; {graph.edges.length} edges
            </span>
          </div>
        )}
      </div>

      {error && <div className="graph-explorer-error">{error}</div>}
      {loading && <div className="graph-explorer-loading">Loading graph...</div>}
      {truncationWarning && (
        <div className="graph-explorer-truncation-warning">
          {truncationWarning}
          <button
            className="graph-explorer-truncation-dismiss"
            onClick={() => setTruncationWarning(null)}
          >
            &times;
          </button>
        </div>
      )}

      {/* Landing page */}
      {isEmpty && !loading && !error && (
        <div className="graph-landing">
          <div className="graph-explorer-empty-icon">
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
              <circle cx="12" cy="12" r="4" />
              <circle cx="36" cy="12" r="4" />
              <circle cx="24" cy="36" r="4" />
              <line x1="15" y1="14" x2="21" y2="33" />
              <line x1="33" y1="14" x2="27" y2="33" />
            </svg>
          </div>
          <div className="graph-landing-tabs">
            <button
              className={`graph-landing-tab${landingMode === 'explore' ? ' active' : ''}`}
              onClick={() => setLandingMode('explore')}
            >
              Explore
            </button>
            <button
              className={`graph-landing-tab${landingMode === 'paths' ? ' active' : ''}`}
              onClick={() => setLandingMode('paths')}
            >
              Find Paths
            </button>
          </div>
          {landingMode === 'explore' ? (
            <>
              <div className="graph-landing-search">
                <GraphSearchBar onSeed={handleSeed} autoFocus />
              </div>
              <div className="graph-landing-chips">
                {QUICK_SEEDS.map((qs) => (
                  <button key={qs.name} className="graph-landing-chip" onClick={() => handleSeed(qs.name, qs.label)}>
                    {qs.name}
                  </button>
                ))}
              </div>
              {recentGraphs.length > 0 && (
                <div className="graph-landing-recent">
                  <div className="graph-landing-recent-title">Recent saved graphs</div>
                  <div className="graph-landing-recent-grid">
                    {recentGraphs.map((sg) => {
                      const badges = countByLabel(sg.graph);
                      return (
                        <div key={sg.id} className="graph-landing-card" onClick={() => handleLoadSavedGraph(sg.id)}>
                          <div className="graph-landing-card-title">{sg.title}</div>
                          <div className="graph-landing-card-badges">
                            {badges.slice(0, 4).map((b) => (
                              <span
                                key={b.label}
                                className="graph-landing-card-badge"
                                style={{ background: `color-mix(in srgb, ${NODE_COLORS[b.label] || '#888'} 30%, transparent)` }}
                              >
                                {b.count} {b.label.replace('MalwareFamily', 'Families').replace('ThreatActor', 'Actors').replace('AttackTechnique', 'Techniques')}
                              </span>
                            ))}
                          </div>
                          <div className="graph-landing-card-meta">
                            {sg.graph.nodes.length} nodes · {relativeDate(sg.updatedAt)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
            </>
          ) : (
            <PathSearchPanel onPathFound={handleLandingPathFound} loading={pathLoading} />
          )}
        </div>
      )}

      {/* Graph canvas + sidebar */}
      {!isEmpty && (
        <div className="graph-explorer-content">
          <div
            className="graph-explorer-canvas-wrapper"
            ref={wrapperRef}
          >
            {/* Path results panel */}
            {pathResult && pathResult.paths.length > 0 && (
              <div className="graph-path-results-panel">
                <div className="graph-path-results-header">
                  <span>{pathResult.meta.message}</span>
                  <button
                    className="graph-path-results-close"
                    onClick={() => { setPathResult(null); setHighlightedPathIndex(null); }}
                    title="Close"
                  >
                    x
                  </button>
                </div>
                <div className="graph-path-results-list">
                  {pathResult.paths.map((p, idx) => (
                    <button
                      key={idx}
                      className={`graph-path-item ${highlightedPathIndex === idx ? 'active' : ''}`}
                      onClick={() => handlePathClick(idx)}
                      title={`Path ${idx + 1}: ${p.length} hop(s)`}
                    >
                      {p.nodes.map((step, si) => (
                        <React.Fragment key={si}>
                          <span
                            className="graph-path-node"
                            style={{ color: NODE_COLORS[step.node_label] ?? '#aaa' }}
                          >
                            {step.node_name.length > 20
                              ? step.node_name.slice(0, 20) + '...'
                              : step.node_name}
                          </span>
                          {si < p.edges.length && (
                            <span className="graph-path-arrow">
                              <span className="graph-path-rel">{p.edges[si]}</span>
                            </span>
                          )}
                        </React.Fragment>
                      ))}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="graph-explorer-canvas">
              <ForceGraph2D
                ref={fgRef as React.MutableRefObject<FGRef | undefined>}
                graphData={graphData}
                width={dimensions.width}
                height={dimensions.height}
                backgroundColor="#0a0a0f"
                nodeCanvasObjectMode={() => 'replace'}
                nodeCanvasObject={nodeCanvasObject}
                nodePointerAreaPaint={nodePointerAreaPaint}
                linkColor={linkColor}
                linkWidth={linkWidth}
                linkLineDash={linkLineDash}
                linkDirectionalParticles={4}
                linkDirectionalParticleWidth={2.5}
                linkDirectionalParticleSpeed={0.005}
                linkDirectionalParticleColor={linkParticleColor}
                linkHoverPrecision={4}
                onNodeClick={handleNodeClick}
                onNodeHover={onNodeHover}
                onNodeDragEnd={onNodeDragEnd}
                onLinkHover={onLinkHover}
                onNodeRightClick={(node, event) => {
                  event.preventDefault();
                  setContextMenu({ x: event.clientX, y: event.clientY, nodeId: node.id as string });
                }}
                onBackgroundClick={() => {
                  setSelectedNode(null);
                  setContextMenu(null);
                  handleNodeUnhover();
                  setTooltip(null);
                }}
                onBackgroundRightClick={(event) => {
                  event.preventDefault();
                  setContextMenu(null);
                }}
                cooldownTicks={100}
                warmupTicks={50}
                enableNodeDrag={true}
                nodeLabel=""
              />
            </div>

            <GraphLegendPanel
              nodes={graph.nodes}
              edges={graph.edges}
              typeFilters={typeFilters}
              onToggleTypeFilter={handleToggleTypeFilter}
              onShowAll={handleShowAll}
              onHideAll={() => {}}
              edgeColorMode={edgeColorMode}
              onToggleEdgeColorMode={() => setEdgeColorMode(m => m === 'source' ? 'rel_type' : 'source')}
              iocSubTypeFilters={iocSubTypeFilters}
              onToggleIocSubType={handleToggleIocSubType}
              dateFrom={filterDateFrom}
              dateTo={filterDateTo}
              onDateRangeChange={handleDateRangeChange}
            />

            {tooltip && (
              <div
                className="malpedia-graph-tooltip"
                style={{
                  left: Math.min(tooltip.x + 10, window.innerWidth - 260),
                  top: Math.min(tooltip.y + 10, window.innerHeight - 200),
                }}
              >
                <div><strong>{tooltip.name}</strong></div>
                <div style={{ opacity: 0.7, fontSize: '11px' }}>{tooltip.type}</div>
                {renderTooltipProps(tooltip.type, tooltip.properties)}
                {tooltip.strategy && (
                  <div style={{ opacity: 0.7, fontSize: '11px' }}>via {tooltip.strategy}</div>
                )}
                {tooltip.score != null && tooltip.score > 0 && (
                  <div style={{ opacity: 0.7, fontSize: '11px' }}>refs: {tooltip.score}</div>
                )}
                {tooltip.neighborCount != null && tooltip.neighborCount > 0 && (
                  <div style={{ opacity: 0.7, fontSize: '11px' }}>{tooltip.neighborSummary}</div>
                )}
              </div>
            )}

            {/* ── Right-click context menu ── */}
            {contextMenu && (() => {
              const cmNode = graph.nodes.find(n => n.id === contextMenu.nodeId);
              if (!cmNode) return null;
              const fgNode = graphData.nodes.find(n => n.id === contextMenu.nodeId);
              const isPinned = fgNode?.fx != null;
              const isIOC = cmNode.label === 'IOC';
              const isCVE = cmNode.label === 'CVE';
              const isTechnique = cmNode.label === 'AttackTechnique';
              const iocType = isIOC ? (cmNode.properties?.ioc_type as string | undefined) : undefined;
              const vtUrl = iocType && VT_TYPES.has(iocType.toUpperCase())
                ? getVirusTotalUrl(iocType, cmNode.name) : null;
              const nvdUrl = isCVE ? getNvdUrl(cmNode.name) : null;
              const attackId = isTechnique ? (cmNode.properties?.attack_id as string | undefined) : undefined;
              const mitreUrl = attackId ? getMitreAttackUrl('MITRE_TECHNIQUE', attackId) : null;

              const expandTag = `expand:${cmNode.id}`;
              const hasExpandedChildren = graph.nodes.some(
                n => n.id !== cmNode.id && n.sourceQueryIds.includes(expandTag),
              );

              return ReactDOM.createPortal(
                <GraphContextMenuInner
                  node={cmNode}
                  position={contextMenu}
                  isPinned={isPinned}
                  hasExpandedChildren={hasExpandedChildren}
                  vtUrl={vtUrl}
                  nvdUrl={nvdUrl}
                  mitreUrl={mitreUrl}
                  onExpand={() => { handleExpand(cmNode.id); setContextMenu(null); }}
                  onExpandDeep={() => { handleExpand(cmNode.id, 2); setContextMenu(null); }}
                  onExpandOnly={(label) => handleExpandOnly(cmNode.id, label)}
                  onCollapse={() => handleCollapse(cmNode.id)}
                  onTogglePin={() => handleTogglePin(cmNode.id)}
                  onFocus={() => { handleFocusNode(cmNode.id); setContextMenu(null); }}
                  onRemove={() => handleRemoveNode(cmNode.id)}
                  onHideType={(label) => { setTypeFilters(prev => ({ ...prev, [label]: false })); setContextMenu(null); }}
                  onFindPathFrom={() => { setPathResult(null); setHighlightedPathIndex(null); setPathPopover({ sourceNode: cmNode, x: contextMenu.x, y: contextMenu.y }); setContextMenu(null); }}
                  onViewDetails={
                    (isIOC || isCVE)
                      ? () => { navigate(`/ioc/${encodeURIComponent(cmNode.name)}`); setContextMenu(null); }
                      : undefined
                  }
                  onClose={() => setContextMenu(null)}
                />,
                document.body,
              );
            })()}

            {/* ── Path search popover ── */}
            {pathPopover && ReactDOM.createPortal(
              <GraphPathSearchPopover
                sourceNode={pathPopover.sourceNode}
                position={pathPopover}
                loading={pathLoading}
                onSelect={(name, label, filters) => handleFindPath(pathPopover.sourceNode, name, label, filters)}
                onClose={() => setPathPopover(null)}
              />,
              document.body,
            )}

            {docBadge && (
              <DocBadgePopover
                entityName={docBadge.name}
                entityLabel={docBadge.label}
                docCount={docBadge.docCount}
                anchorEl={docBadge.el}
                onClose={() => setDocBadge(null)}
              />
            )}
          </div>

          {/* Drag handle */}
          <div
            className={`graph-sidebar-drag-handle ${isSidebarDragging ? 'active' : ''}`}
            onMouseDown={handleSidebarDragStart}
          />

          {/* Always-visible sidebar */}
          <div style={{ width: sidebarWidth, flexShrink: 0 }}>
            <GraphSidebar
              selectedNode={selectedNode}
              graph={graph}
              onFocusNode={handleFocusNode}
              onSelectNode={(node) => setSelectedNode(node)}
              onDeselectNode={() => setSelectedNode(null)}
              messages={detailMessages}
              onShowDocs={selectedNode ? (el) => {
                setDocBadge({
                  name: selectedNode.name,
                  label: selectedNode.label,
                  docCount: selectedNode.score ?? 0,
                  el,
                });
              } : undefined}
              docCount={selectedNode?.score ?? undefined}
              expansionSettings={expansionSettings}
              onExpansionSettingsChange={updateExpansionSettings}
            />
          </div>
        </div>
      )}
    </div>
  );
}
